import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { CheckCircle2, Heart, ArrowRight } from 'lucide-react';

export const IVFFertilityAnalytics = () => {
  const relatedServices = [
    {
      title: "Healthcare Performance Analytics",
      url: "/services/healthcare-performance-analytics",
      description: "Executive dashboards and performance analytics for hospitals and clinics"
    },
    {
      title: "Clinical Quality & Compliance Analytics",
      url: "/services/clinical-quality-compliance-analytics",
      description: "Accreditation-ready quality and compliance analytics for UAE healthcare"
    },
    {
      title: "Predictive Analytics & Decision Intelligence",
      url: "/services/predictive-analytics-decision-intelligence",
      description: "Predictive modelling and machine learning for healthcare"
    },
    {
      title: "Research & Biostatistics",
      url: "/services/research-biostatistics",
      description: "End-to-end biostatistics support from study design to publication"
    }
  ];

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "IVF & Fertility Analytics",
    "description": "Turn IVF cycle data into higher success rates and fewer cancellations. Biostatistics-led fertility analytics for UAE clinics.",
    "provider": {
      "@type": "Organization",
      "name": "StatZen Analytics",
      "url": "https://statzenanalytics.com"
    },
    "areaServed": {
      "@type": "Place",
      "name": "United Arab Emirates"
    },
    "serviceType": "Healthcare Analytics"
  };

  return (
    <div className="min-h-screen">
      <Helmet>
        <title>IVF & Fertility Analytics for UAE Clinics | StatZen</title>
        <meta name="description" content="Turn IVF cycle data into higher success rates and fewer cancellations. Biostatistics-led fertility analytics for UAE clinics. ESHRE and ISAR benchmarking." />
        <link rel="canonical" href="https://statzenanalytics.com/services/ivf-fertility-analytics" />
        <script type="application/ld+json">
          {JSON.stringify(serviceSchema)}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="pt-32 pb-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-blue-50 to-white">
        <div className="max-w-5xl mx-auto">
          <Link to="/services" className="inline-flex items-center text-blue-600 hover:text-blue-700 mb-6 transition-colors">
            <ArrowRight className="w-4 h-4 mr-2 rotate-180" />
            Back to All Services
          </Link>
          <div className="flex items-start space-x-6 mb-8">
            <div className="w-20 h-20 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <Heart className="w-10 h-10 text-blue-600" />
            </div>
            <div>
              <div className="text-4xl font-bold text-blue-600 mb-2">01</div>
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                IVF & Fertility Analytics for UAE Clinics
              </h1>
              <p className="text-xl text-gray-700 italic">
                Improve IVF Success. Reduce Dropouts. Maximize Clinic Performance.
              </p>
            </div>
          </div>
          <p className="text-lg text-gray-600 leading-relaxed">
            Designed exclusively for fertility clinics and IVF centres, this solution helps you understand exactly what's driving clinical outcomes, patient retention, and operational performance.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
            {/* We Help You */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">We Help You:</h2>
              <ul className="space-y-3">
                <li className="flex items-start space-x-3">
                  <CheckCircle2 className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">IVF Success Rate Analysis</span>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle2 className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Patient Journey & Dropout Analytics</span>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle2 className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Consultation-to-Treatment Conversion Analysis</span>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle2 className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Cycle Cancellation Root Cause Analysis</span>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle2 className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Embryology Laboratory Performance Analytics</span>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle2 className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Annual Success Rate Reporting</span>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle2 className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">ESHRE & ISAR Benchmarking</span>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle2 className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">DHA / DOH / MOH Reporting Support</span>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle2 className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Predictive Treatment Outcome Models</span>
                </li>
              </ul>
            </div>

            {/* Ideal For & Outcomes */}
            <div className="space-y-8">
              <div className="bg-blue-50 p-6 rounded-lg">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Ideal For:</h3>
                <p className="text-gray-700">
                  IVF Clinics, Fertility Centres, Embryology Laboratories
                </p>
              </div>

              <div className="border-l-4 border-teal-500 pl-6 py-4 bg-teal-50 rounded-r-lg">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Business Outcomes:</h3>
                <ul className="space-y-3">
                  <li className="flex items-start space-x-3">
                    <CheckCircle2 className="w-5 h-5 text-teal-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Increase patient retention</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <CheckCircle2 className="w-5 h-5 text-teal-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Improve pregnancy and live birth outcomes</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <CheckCircle2 className="w-5 h-5 text-teal-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Reduce cycle cancellations</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <CheckCircle2 className="w-5 h-5 text-teal-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Improve operational efficiency</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <CheckCircle2 className="w-5 h-5 text-teal-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Generate board-ready performance reports</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg p-8 text-center mb-16">
            <h2 className="text-3xl font-bold text-white mb-4">
              Ready to Turn Your IVF Data Into Better Outcomes?
            </h2>
            <p className="text-blue-100 mb-6 max-w-2xl mx-auto">
              Book a free 30-minute consultation to discuss your clinic's data and identify the highest-impact analytics opportunities.
            </p>
            <Link to="/contact">
              <button className="bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors">
                Book a Free Consultation
              </button>
            </Link>
          </div>

          {/* Related Services */}
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Related Services</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {relatedServices.map((service, index) => (
                <Link key={index} to={service.url}>
                  <Card className="h-full hover:shadow-lg transition-shadow">
                    <CardContent className="p-6">
                      <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-blue-600">
                        {service.title}
                      </h3>
                      <p className="text-sm text-gray-600">{service.description}</p>
                      <div className="mt-4 text-blue-600 text-sm font-semibold flex items-center">
                        Learn More <ArrowRight className="w-4 h-4 ml-2" />
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default IVFFertilityAnalytics;
